/**
 * A minimal ZIP reader, so the playback loader accepts the round zip exactly
 * as the moderation downloaded it — no unpacking by hand at the venue, and
 * no dependency added to a bundle that has to work offline.
 *
 * Only what our own export writes is supported: stored (0) and deflated (8)
 * entries, no encryption, no spanning. Inflation uses the platform's
 * `DecompressionStream('deflate-raw')`; if a browser lacks it, the loader
 * falls back to asking for the two JSON files, which need no unpacking.
 */

export interface ZipEntry {
  name: string;
  bytes: Uint8Array;
}

const EOCD_SIG = 0x06054b50;
const CDIR_SIG = 0x02014b50;

export function inflateAvailable(): boolean {
  return typeof DecompressionStream !== 'undefined';
}

export async function unzip(buffer: ArrayBuffer): Promise<ZipEntry[]> {
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);
  const eocd = findEocd(view);
  if (eocd < 0) throw new Error('Das ist kein Zip-Archiv (Endeintrag fehlt).');

  const count = view.getUint16(eocd + 10, true);
  let offset = view.getUint32(eocd + 16, true);
  const entries: ZipEntry[] = [];

  for (let i = 0; i < count; i++) {
    if (view.getUint32(offset, true) !== CDIR_SIG) break;
    const method = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const nameLen = view.getUint16(offset + 28, true);
    const extraLen = view.getUint16(offset + 30, true);
    const commentLen = view.getUint16(offset + 32, true);
    const localOffset = view.getUint32(offset + 42, true);
    const name = new TextDecoder().decode(bytes.subarray(offset + 46, offset + 46 + nameLen));
    offset += 46 + nameLen + extraLen + commentLen;

    // The local header repeats the name and may carry a different extra field.
    const localNameLen = view.getUint16(localOffset + 26, true);
    const localExtraLen = view.getUint16(localOffset + 28, true);
    const dataStart = localOffset + 30 + localNameLen + localExtraLen;
    const raw = bytes.subarray(dataStart, dataStart + compressedSize);

    if (name.endsWith('/')) continue; // directory entry
    entries.push({ name, bytes: method === 0 ? raw : await inflateRaw(raw) });
  }
  return entries;
}

function findEocd(view: DataView): number {
  const min = Math.max(0, view.byteLength - 65_557);
  for (let i = view.byteLength - 22; i >= min; i--) {
    if (view.getUint32(i, true) === EOCD_SIG) return i;
  }
  return -1;
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  if (!inflateAvailable()) {
    throw new Error('Dieser Browser kann Zip-Dateien nicht entpacken — bitte die beiden JSON-Dateien laden.');
  }
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}
